// src/pages/MatchDetailPage.jsx
import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import Header from '../components/layout/Header';
import Footer from '../components/layout/Footer';
import { FootballAPI, formatOddsValue } from '../services/footballApi';
import { useBetSlip } from '../context/BetSlipContext';
import { 
    FiArrowLeft, FiCalendar, FiMapPin, FiUser, FiUsers, FiClock,
    FiRefreshCw, FiHome, FiTrendingUp, FiTarget, FiShield,
    FiBarChart2, FiAward, FiZap, FiActivity, FiStar, FiInfo,
    FiTrello, FiPieChart, FiCornerUpRight, FiFlag, FiChevronDown,
    FiChevronUp
} from 'react-icons/fi';

const MatchDetailPage = () => {
    const { fixtureId } = useParams();
    const navigate = useNavigate();
    const { addToBetSlip, bets } = useBetSlip();
    
    const [matchData, setMatchData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [oddsFormat, setOddsFormat] = useState('decimal');
    const [activeTab, setActiveTab] = useState('odds');
    const [refreshing, setRefreshing] = useState(false);
    const [expandedMarkets, setExpandedMarkets] = useState({});
    
    useEffect(() => {
        fetchMatchDetails();
        
        let interval;
        if (matchData?.fixture?.status && ['1H', 'HT', '2H', 'ET', 'P'].includes(matchData.fixture.status)) {
            interval = setInterval(() => fetchMatchDetails(true), 30000);
        }
        
        return () => interval && clearInterval(interval);
    }, [fixtureId]);
    
    const fetchMatchDetails = async (silent = false) => {
        if (!silent) setLoading(true);
        try {
            const response = await FootballAPI.getMatchDetails(fixtureId);
            if (response.success && response.data) {
                setMatchData(response.data);
            } else {
                setError(response.error || 'Match not found');
            }
        } catch (err) {
            setError('Failed to load match details');
        } finally {
            if (!silent) setLoading(false);
        }
    };
    
    const isBetSelected = (selectionName, marketType) => {
        return bets.some(bet => 
            bet.matchId === parseInt(fixtureId) &&
            bet.selection === selectionName &&
            bet.marketType === marketType
        );
    };

    const toggleMarket = (marketId) => {
        setExpandedMarkets(prev => ({
            ...prev,
            [marketId]: !prev[marketId]
        }));
    };

    // Initialize all markets as expanded by default
    useEffect(() => {
        if (matchData) {
            const allMarkets = ['match_winner', 'double_chance', 'over_under', 'btts', 
                'first_half', 'second_half', 'handicap', 'correct_score', 
                'first_goal', 'ht_ft', 'total_goals'];
            const expanded = {};
            allMarkets.forEach(m => expanded[m] = true);
            setExpandedMarkets(expanded);
        }
    }, [matchData]);
    
    if (loading) {
        return (
            <div className="min-h-screen bg-deep-black pt-16">
                <Header />
                <div className="flex items-center justify-center h-96">
                    <div className="text-center">
                        <div className="inline-block animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
                        <p className="text-gray-400 mt-4">Loading match details...</p>
                    </div>
                </div>
                <Footer />
            </div>
        );
    }
    
    if (error || !matchData) {
        return (
            <div className="min-h-screen bg-deep-black pt-16">
                <Header />
                <div className="max-w-7xl mx-auto px-4 py-8">
                    <div className="bg-red-900/50 border border-red-700 rounded-lg p-8 text-center">
                        <div className="text-6xl mb-4">⚠️</div>
                        <h2 className="text-2xl font-bold text-white mb-2">Match Not Found</h2>
                        <p className="text-gray-400 mb-6">{error || "The match you're looking for doesn't exist."}</p>
                        <Link to="/sports" className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-3 rounded-lg font-semibold">
                            Back to Sports
                        </Link>
                    </div>
                </div>
                <Footer />
            </div>
        );
    }
    
    const { fixture, odds, h2h, lineups } = matchData;
    const isLive = ['1H', 'HT', '2H', 'ET', 'P'].includes(fixture.status);
    const matchDate = new Date(fixture.fixture_date);
    const formattedDate = matchDate.toLocaleDateString('en-US', { 
        weekday: 'long', year: 'numeric', month: 'long', day: 'numeric',
        hour: '2-digit', minute: '2-digit'
    });
    
    // Parse all odds markets
    const matchWinner = odds?.match_winner || {};
    const overUnder = odds?.over_under || {};
    const btts = odds?.btts || {};
    const doubleChance = odds?.double_chance || {};
    const firstHalfWinner = odds?.first_half_winner || {};
    const secondHalfWinner = odds?.second_half_winner || {};
    const handicap = odds?.handicap || {};
    const correctScore = odds?.correct_score ? JSON.parse(odds.correct_score.correct_score_odds || '{}') : {};
    const halfTimeFullTime = odds?.halftime_fulltime ? JSON.parse(odds.halftime_fulltime.halftime_fulltime_odds || '{}') : {};
    const firstGoal = odds?.first_goal ? JSON.parse(odds.first_goal.first_goal_odds || '{}') : {};
    const totalGoals = odds?.total_goals ? JSON.parse(odds.total_goals.total_goals_odds || '{}') : {};
    
    // Available markets configuration
    const markets = [
        { id: 'match_winner', name: 'Match Winner', icon: <FiHome className="text-blue-500" />, hasOdds: matchWinner.home_odds || matchWinner.draw_odds || matchWinner.away_odds },
        { id: 'double_chance', name: 'Double Chance', icon: <FiShield className="text-purple-500" />, hasOdds: doubleChance.home_draw_odds || doubleChance.home_away_odds || doubleChance.draw_away_odds },
        { id: 'over_under', name: 'Over/Under', icon: <FiBarChart2 className="text-green-500" />, hasOdds: overUnder.over_odds || overUnder.under_odds },
        { id: 'btts', name: 'Both Teams Score', icon: <FiTarget className="text-red-500" />, hasOdds: btts.btts_yes_odds || btts.btts_no_odds },
        { id: 'first_half', name: '1st Half Winner', icon: <FiActivity className="text-yellow-500" />, hasOdds: firstHalfWinner.home_odds || firstHalfWinner.draw_odds || firstHalfWinner.away_odds },
        { id: 'second_half', name: '2nd Half Winner', icon: <FiTrendingUp className="text-orange-500" />, hasOdds: secondHalfWinner.home_odds || secondHalfWinner.draw_odds || secondHalfWinner.away_odds },
        { id: 'handicap', name: 'Handicap', icon: <FiZap className="text-indigo-500" />, hasOdds: handicap.home_handicap_odds || handicap.away_handicap_odds },
        { id: 'correct_score', name: 'Correct Score', icon: <FiAward className="text-pink-500" />, hasOdds: Object.keys(correctScore).length > 0 },
        { id: 'first_goal', name: 'First Goal', icon: <FiTarget className="text-cyan-500" />, hasOdds: firstGoal.home || firstGoal.away },
        { id: 'ht_ft', name: 'HT/FT', icon: <FiClock className="text-teal-500" />, hasOdds: Object.keys(halfTimeFullTime).length > 0 },
        { id: 'total_goals', name: 'Total Goals', icon: <FiBarChart2 className="text-emerald-500" />, hasOdds: Object.keys(totalGoals).length > 0 },
    ].filter(m => m.hasOdds);
    
    // Mock statistics
    const matchStats = {
        possession: { home: 55, away: 45 },
        shots: { home: 12, away: 8 },
        shotsOnTarget: { home: 5, away: 3 },
        corners: { home: 6, away: 4 },
        fouls: { home: 10, away: 12 },
        yellowCards: { home: 2, away: 3 },
        redCards: { home: 0, away: 0 }
    };
    
    return (
        <div className="min-h-screen bg-deep-black pt-14 sm:pt-16">
            <Header />
            
            <div className="max-w-7xl mx-auto px-3 sm:px-4 lg:px-8 py-3 sm:py-6">
                {/* Navigation */}
                <div className="flex flex-wrap items-center justify-between gap-3 mb-4 sm:mb-6">
                    <button onClick={() => navigate('/sports')} className="flex items-center gap-1 sm:gap-2 text-gray-400 hover:text-white transition text-sm sm:text-base">
                        <FiArrowLeft className="w-4 h-4" />
                        <span>Back</span>
                    </button>
                    
                    <div className="flex items-center gap-2">
                        <select 
                            value={oddsFormat}
                            onChange={(e) => setOddsFormat(e.target.value)}
                            className="bg-gray-800 text-white px-2 sm:px-3 py-1.5 sm:py-2 rounded-lg text-xs sm:text-sm"
                        >
                            <option value="decimal">Decimal</option>
                            <option value="fractional">Fractional</option>
                            <option value="american">American</option>
                        </select>
                        
                        <button onClick={() => fetchMatchDetails()} className="flex items-center gap-1 sm:gap-2 bg-gray-800 hover:bg-gray-700 text-white px-2 sm:px-3 py-1.5 sm:py-2 rounded-lg text-xs sm:text-sm">
                            <FiRefreshCw className={`w-3 h-3 sm:w-4 sm:h-4 ${refreshing ? 'animate-spin' : ''}`} />
                            <span className="hidden xs:inline">Refresh</span>
                        </button>
                    </div>
                </div>
                
                {/* Match Header */}
                <div className="bg-gradient-to-r from-gray-900 to-gray-800 rounded-xl p-4 sm:p-6 mb-4 sm:mb-6">
                {/* League Info - Horizontal on all devices */}
                <div className="flex items-center justify-center gap-2 mb-3 sm:mb-4 flex-wrap">
                    {fixture.league_logo && <img src={fixture.league_logo} className="w-5 h-5 sm:w-6 sm:h-6 object-contain" />}
                    <span className="text-xs sm:text-sm text-gray-400">{fixture.league_name}</span>
                    {fixture.round && <span className="text-[10px] sm:text-xs text-gray-500">• {fixture.round}</span>}
                    {isLive && <span className="bg-red-600 text-white text-[10px] sm:text-xs px-2 py-0.5 sm:py-1 rounded-full animate-pulse">LIVE</span>}
                </div>
                
                {/* Match Teams - Horizontal on mobile (flex-row) */}
                <div className="flex flex-row items-center justify-between gap-2 sm:gap-6">
                    {/* Home Team */}
                    <div className="flex-1 text-center min-w-0">
                        {fixture.home_team_logo && (
                            <img 
                                src={fixture.home_team_logo} 
                                className="w-10 h-10 sm:w-20 sm:h-20 object-contain mx-auto mb-1 sm:mb-3" 
                                alt={fixture.home_team}
                            />
                        )}
                        <h2 className="text-xs sm:text-2xl font-bold text-white truncate">{fixture.home_team}</h2>
                    </div>
                    
                    {/* Score / VS - Centered */}
                    <div className="text-center flex-shrink-0 px-1 sm:px-4">
                        {isLive && (fixture.home_score !== null || fixture.away_score !== null) ? (
                            <>
                                <div className="text-xl sm:text-5xl font-bold text-white mb-0.5 sm:mb-2">
                                    {fixture.home_score ?? 0} - {fixture.away_score ?? 0}
                                </div>
                                <div className="text-[10px] sm:text-sm text-gray-400">LIVE {fixture.elapsed}'</div>
                            </>
                        ) : (
                            <>
                                <div className="text-lg sm:text-3xl font-bold text-gray-400 mb-0.5 sm:mb-2">VS</div>
                                <div className="text-[8px] sm:text-sm text-gray-500 hidden sm:block">{formattedDate}</div>
                            </>
                        )}
                    </div>
                    
                    {/* Away Team */}
                    <div className="flex-1 text-center min-w-0">
                        {fixture.away_team_logo && (
                            <img 
                                src={fixture.away_team_logo} 
                                className="w-10 h-10 sm:w-20 sm:h-20 object-contain mx-auto mb-1 sm:mb-3" 
                                alt={fixture.away_team}
                            />
                        )}
                        <h2 className="text-xs sm:text-2xl font-bold text-white truncate">{fixture.away_team}</h2>
                    </div>
                </div>
                
                {/* Match Info - Horizontal grid on mobile */}
                <div className="mt-3 sm:mt-6 pt-3 sm:pt-6 border-t border-gray-700 grid grid-cols-3 gap-1 sm:gap-4 text-[10px] sm:text-sm">
                    {fixture.stadium && (
                        <div className="flex items-center justify-center gap-0.5 sm:gap-2 text-gray-400 min-w-0">
                            <FiMapPin className="w-2.5 h-2.5 sm:w-4 sm:h-4 flex-shrink-0" />
                            <span className="truncate hidden xs:inline">{fixture.stadium}</span>
                            <span className="truncate xs:hidden">{fixture.stadium?.split(' ').slice(0, 2).join(' ') || ''}</span>
                        </div>
                    )}
                    {fixture.referee && (
                        <div className="flex items-center justify-center gap-0.5 sm:gap-2 text-gray-400 min-w-0">
                            <FiUser className="w-2.5 h-2.5 sm:w-4 sm:h-4 flex-shrink-0" />
                            <span className="truncate hidden sm:inline">Referee: {fixture.referee}</span>
                            <span className="truncate sm:hidden">{fixture.referee?.split(' ').slice(0, 1).join('') || ''}</span>
                        </div>
                    )}
                    <div className="flex items-center justify-center gap-0.5 sm:gap-2 text-gray-400 min-w-0">
                        <FiCalendar className="w-2.5 h-2.5 sm:w-4 sm:h-4 flex-shrink-0" />
                        <span className="truncate text-[8px] xs:text-[10px] sm:text-sm">
                            {isLive ? (
                                <span className="text-red-400 animate-pulse">LIVE</span>
                            ) : (
                                matchDate.toLocaleDateString('en-US', { 
                                    month: 'short', 
                                    day: 'numeric',
                                    hour: '2-digit',
                                    minute: '2-digit'
                                })
                            )}
                        </span>
                    </div>
                </div>
            </div>
                
                {/* Main Tabs */}
                <div className="flex gap-1 sm:gap-2 mb-4 sm:mb-6 border-b border-gray-800 overflow-x-auto pb-1">
                    <button
                        onClick={() => setActiveTab('odds')}
                        className={`px-3 sm:px-4 py-1.5 sm:py-2 text-xs sm:text-sm font-medium transition whitespace-nowrap ${
                            activeTab === 'odds' 
                                ? 'text-blue-500 border-b-2 border-blue-500' 
                                : 'text-gray-400 hover:text-white'
                        }`}
                    >
                        Odds
                    </button>
                    <button
                        onClick={() => setActiveTab('h2h')}
                        className={`px-3 sm:px-4 py-1.5 sm:py-2 text-xs sm:text-sm font-medium transition whitespace-nowrap ${
                            activeTab === 'h2h' 
                                ? 'text-blue-500 border-b-2 border-blue-500' 
                                : 'text-gray-400 hover:text-white'
                        }`}
                    >
                        H2H
                    </button>
                    <button
                        onClick={() => setActiveTab('stats')}
                        className={`px-3 sm:px-4 py-1.5 sm:py-2 text-xs sm:text-sm font-medium transition whitespace-nowrap ${
                            activeTab === 'stats' 
                                ? 'text-blue-500 border-b-2 border-blue-500' 
                                : 'text-gray-400 hover:text-white'
                        }`}
                    >
                        Stats
                    </button>
                    <button
                        onClick={() => setActiveTab('lineups')}
                        className={`px-3 sm:px-4 py-1.5 sm:py-2 text-xs sm:text-sm font-medium transition whitespace-nowrap ${
                            activeTab === 'lineups' 
                                ? 'text-blue-500 border-b-2 border-blue-500' 
                                : 'text-gray-400 hover:text-white'
                        }`}
                    >
                        Lineups
                    </button>
                </div>
                
                {/* Tab Content */}
                {activeTab === 'odds' && (
                    <div className="space-y-4 sm:space-y-6">
                        {/* All Markets Displayed At Once */}
                        {markets.map(market => {
                            const isExpanded = expandedMarkets[market.id] !== false;
                            
                            return (
                                <div key={market.id} className="bg-gray-900 rounded-xl overflow-hidden">
                                    {/* Market Header - Collapsible */}
                                    <button
                                        onClick={() => toggleMarket(market.id)}
                                        className="w-full flex items-center justify-between p-3 sm:p-4 bg-gray-800/50 hover:bg-gray-800 transition-colors"
                                    >
                                        <div className="flex items-center gap-2 sm:gap-3">
                                            <span className="text-lg sm:text-xl">{market.icon}</span>
                                            <span className="text-sm sm:text-base font-semibold text-white">{market.name}</span>
                                        </div>
                                        <div className="flex items-center gap-2">
                                            <span className="text-xs text-gray-500">Click to {isExpanded ? 'collapse' : 'expand'}</span>
                                            {isExpanded ? 
                                                <FiChevronUp className="w-4 h-4 sm:w-5 sm:h-5 text-gray-400" /> : 
                                                <FiChevronDown className="w-4 h-4 sm:w-5 sm:h-5 text-gray-400" />
                                            }
                                        </div>
                                    </button>
                                    
                                    {/* Market Content */}
                                    {isExpanded && (
                                        <div className="p-3 sm:p-4">
                                            {/* Match Winner (1X2) */}
                                            {market.id === 'match_winner' && (
                                                <div className="grid grid-cols-3 gap-2 sm:gap-4">
                                                    <OddsCard 
                                                        title={fixture.home_team} 
                                                        odds={matchWinner.home_odds} 
                                                        oddsFormat={oddsFormat}
                                                        isSelected={isBetSelected(fixture.home_team, 'match_winner')}
                                                        onSelect={() => addToBetSlip({ 
                                                            matchId: fixture.id, 
                                                            match: `${fixture.home_team} vs ${fixture.away_team}`, 
                                                            selection: fixture.home_team, 
                                                            odds: parseFloat(matchWinner.home_odds), 
                                                            marketType: 'match_winner', 
                                                            sport: 'football' 
                                                        })}
                                                    />
                                                    <OddsCard 
                                                        title="Draw" 
                                                        odds={matchWinner.draw_odds} 
                                                        oddsFormat={oddsFormat}
                                                        isSelected={isBetSelected('Draw', 'match_winner')}
                                                        onSelect={() => addToBetSlip({ 
                                                            matchId: fixture.id, 
                                                            match: `${fixture.home_team} vs ${fixture.away_team}`, 
                                                            selection: 'Draw', 
                                                            odds: parseFloat(matchWinner.draw_odds), 
                                                            marketType: 'match_winner', 
                                                            sport: 'football' 
                                                        })}
                                                    />
                                                    <OddsCard 
                                                        title={fixture.away_team} 
                                                        odds={matchWinner.away_odds} 
                                                        oddsFormat={oddsFormat}
                                                        isSelected={isBetSelected(fixture.away_team, 'match_winner')}
                                                        onSelect={() => addToBetSlip({ 
                                                            matchId: fixture.id, 
                                                            match: `${fixture.home_team} vs ${fixture.away_team}`, 
                                                            selection: fixture.away_team, 
                                                            odds: parseFloat(matchWinner.away_odds), 
                                                            marketType: 'match_winner', 
                                                            sport: 'football' 
                                                        })}
                                                    />
                                                </div>
                                            )}
                                            
                                            {/* Double Chance */}
                                            {market.id === 'double_chance' && (
                                                <div className="grid grid-cols-3 gap-2 sm:gap-4">
                                                    <OddsCard title="Home/Draw" odds={doubleChance.home_draw_odds} oddsFormat={oddsFormat}
                                                        isSelected={isBetSelected('Home/Draw', 'double_chance')}
                                                        onSelect={() => addToBetSlip({ matchId: fixture.id, match: `${fixture.home_team} vs ${fixture.away_team}`, selection: 'Home/Draw', odds: parseFloat(doubleChance.home_draw_odds), marketType: 'double_chance', sport: 'football' })} />
                                                    <OddsCard title="Home/Away" odds={doubleChance.home_away_odds} oddsFormat={oddsFormat}
                                                        isSelected={isBetSelected('Home/Away', 'double_chance')}
                                                        onSelect={() => addToBetSlip({ matchId: fixture.id, match: `${fixture.home_team} vs ${fixture.away_team}`, selection: 'Home/Away', odds: parseFloat(doubleChance.home_away_odds), marketType: 'double_chance', sport: 'football' })} />
                                                    <OddsCard title="Draw/Away" odds={doubleChance.draw_away_odds} oddsFormat={oddsFormat}
                                                        isSelected={isBetSelected('Draw/Away', 'double_chance')}
                                                        onSelect={() => addToBetSlip({ matchId: fixture.id, match: `${fixture.home_team} vs ${fixture.away_team}`, selection: 'Draw/Away', odds: parseFloat(doubleChance.draw_away_odds), marketType: 'double_chance', sport: 'football' })} />
                                                </div>
                                            )}
                                            
                                            {/* Over/Under */}
                                            {market.id === 'over_under' && (
                                                <div className="grid grid-cols-2 gap-2 sm:gap-4">
                                                    <OddsCard title="Over 2.5" odds={overUnder.over_odds} oddsFormat={oddsFormat}
                                                        isSelected={isBetSelected('Over 2.5', 'over_under')}
                                                        onSelect={() => addToBetSlip({ matchId: fixture.id, match: `${fixture.home_team} vs ${fixture.away_team}`, selection: 'Over 2.5', odds: parseFloat(overUnder.over_odds), marketType: 'over_under', sport: 'football' })} />
                                                    <OddsCard title="Under 2.5" odds={overUnder.under_odds} oddsFormat={oddsFormat}
                                                        isSelected={isBetSelected('Under 2.5', 'over_under')}
                                                        onSelect={() => addToBetSlip({ matchId: fixture.id, match: `${fixture.home_team} vs ${fixture.away_team}`, selection: 'Under 2.5', odds: parseFloat(overUnder.under_odds), marketType: 'over_under', sport: 'football' })} />
                                                </div>
                                            )}
                                            
                                            {/* BTTS */}
                                            {market.id === 'btts' && (
                                                <div className="grid grid-cols-2 gap-2 sm:gap-4">
                                                    <OddsCard title="Yes" odds={btts.btts_yes_odds} oddsFormat={oddsFormat}
                                                        isSelected={isBetSelected('BTTS Yes', 'btts')}
                                                        onSelect={() => addToBetSlip({ matchId: fixture.id, match: `${fixture.home_team} vs ${fixture.away_team}`, selection: 'BTTS Yes', odds: parseFloat(btts.btts_yes_odds), marketType: 'btts', sport: 'football' })} />
                                                    <OddsCard title="No" odds={btts.btts_no_odds} oddsFormat={oddsFormat}
                                                        isSelected={isBetSelected('BTTS No', 'btts')}
                                                        onSelect={() => addToBetSlip({ matchId: fixture.id, match: `${fixture.home_team} vs ${fixture.away_team}`, selection: 'BTTS No', odds: parseFloat(btts.btts_no_odds), marketType: 'btts', sport: 'football' })} />
                                                </div>
                                            )}
                                            
                                            {/* First Half Winner */}
                                            {market.id === 'first_half' && (
                                                <div className="grid grid-cols-3 gap-2 sm:gap-4">
                                                    <OddsCard title={fixture.home_team} odds={firstHalfWinner.home_odds} oddsFormat={oddsFormat}
                                                        isSelected={isBetSelected(`${fixture.home_team} (1H)`, 'first_half')}
                                                        onSelect={() => addToBetSlip({ matchId: fixture.id, match: `${fixture.home_team} vs ${fixture.away_team}`, selection: `${fixture.home_team} (1H)`, odds: parseFloat(firstHalfWinner.home_odds), marketType: 'first_half', sport: 'football' })} />
                                                    <OddsCard title="Draw" odds={firstHalfWinner.draw_odds} oddsFormat={oddsFormat}
                                                        isSelected={isBetSelected('Draw (1H)', 'first_half')}
                                                        onSelect={() => addToBetSlip({ matchId: fixture.id, match: `${fixture.home_team} vs ${fixture.away_team}`, selection: 'Draw (1H)', odds: parseFloat(firstHalfWinner.draw_odds), marketType: 'first_half', sport: 'football' })} />
                                                    <OddsCard title={fixture.away_team} odds={firstHalfWinner.away_odds} oddsFormat={oddsFormat}
                                                        isSelected={isBetSelected(`${fixture.away_team} (1H)`, 'first_half')}
                                                        onSelect={() => addToBetSlip({ matchId: fixture.id, match: `${fixture.home_team} vs ${fixture.away_team}`, selection: `${fixture.away_team} (1H)`, odds: parseFloat(firstHalfWinner.away_odds), marketType: 'first_half', sport: 'football' })} />
                                                </div>
                                            )}
                                            
                                            {/* Second Half Winner */}
                                            {market.id === 'second_half' && (
                                                <div className="grid grid-cols-3 gap-2 sm:gap-4">
                                                    <OddsCard title={fixture.home_team} odds={secondHalfWinner.home_odds} oddsFormat={oddsFormat}
                                                        isSelected={isBetSelected(`${fixture.home_team} (2H)`, 'second_half')}
                                                        onSelect={() => addToBetSlip({ matchId: fixture.id, match: `${fixture.home_team} vs ${fixture.away_team}`, selection: `${fixture.home_team} (2H)`, odds: parseFloat(secondHalfWinner.home_odds), marketType: 'second_half', sport: 'football' })} />
                                                    <OddsCard title="Draw" odds={secondHalfWinner.draw_odds} oddsFormat={oddsFormat}
                                                        isSelected={isBetSelected('Draw (2H)', 'second_half')}
                                                        onSelect={() => addToBetSlip({ matchId: fixture.id, match: `${fixture.home_team} vs ${fixture.away_team}`, selection: 'Draw (2H)', odds: parseFloat(secondHalfWinner.draw_odds), marketType: 'second_half', sport: 'football' })} />
                                                    <OddsCard title={fixture.away_team} odds={secondHalfWinner.away_odds} oddsFormat={oddsFormat}
                                                        isSelected={isBetSelected(`${fixture.away_team} (2H)`, 'second_half')}
                                                        onSelect={() => addToBetSlip({ matchId: fixture.id, match: `${fixture.home_team} vs ${fixture.away_team}`, selection: `${fixture.away_team} (2H)`, odds: parseFloat(secondHalfWinner.away_odds), marketType: 'second_half', sport: 'football' })} />
                                                </div>
                                            )}
                                            
                                            {/* Handicap */}
                                            {market.id === 'handicap' && (
                                                <div className="grid grid-cols-2 gap-2 sm:gap-4">
                                                    <OddsCard title={`${fixture.home_team} ${handicap.handicap_value || ''}`} odds={handicap.home_handicap_odds} oddsFormat={oddsFormat}
                                                        isSelected={isBetSelected(fixture.home_team, 'handicap')}
                                                        onSelect={() => addToBetSlip({ matchId: fixture.id, match: `${fixture.home_team} vs ${fixture.away_team}`, selection: fixture.home_team, odds: parseFloat(handicap.home_handicap_odds), marketType: 'handicap', sport: 'football' })} />
                                                    <OddsCard title={`${fixture.away_team} ${handicap.handicap_value ? `-${handicap.handicap_value}` : ''}`} odds={handicap.away_handicap_odds} oddsFormat={oddsFormat}
                                                        isSelected={isBetSelected(fixture.away_team, 'handicap')}
                                                        onSelect={() => addToBetSlip({ matchId: fixture.id, match: `${fixture.home_team} vs ${fixture.away_team}`, selection: fixture.away_team, odds: parseFloat(handicap.away_handicap_odds), marketType: 'handicap', sport: 'football' })} />
                                                </div>
                                            )}
                                            
                                            {/* Correct Score */}
                                            {market.id === 'correct_score' && Object.keys(correctScore).length > 0 && (
                                                <div className="grid grid-cols-2 xs:grid-cols-3 sm:grid-cols-4 gap-2 sm:gap-3">
                                                    {Object.entries(correctScore).slice(0, 12).map(([score, oddsVal]) => (
                                                        <OddsCard key={score} title={score} odds={oddsVal} oddsFormat={oddsFormat} compact
                                                            isSelected={isBetSelected(score, 'correct_score')}
                                                            onSelect={() => addToBetSlip({ matchId: fixture.id, match: `${fixture.home_team} vs ${fixture.away_team}`, selection: score, odds: parseFloat(oddsVal), marketType: 'correct_score', sport: 'football' })} />
                                                    ))}
                                                </div>
                                            )}
                                            
                                            {/* First Goal */}
                                            {market.id === 'first_goal' && (
                                                <div className="grid grid-cols-3 gap-2 sm:gap-4">
                                                    <OddsCard title={fixture.home_team} odds={firstGoal.home} oddsFormat={oddsFormat}
                                                        isSelected={isBetSelected(`${fixture.home_team} (First Goal)`, 'first_goal')}
                                                        onSelect={() => addToBetSlip({ matchId: fixture.id, match: `${fixture.home_team} vs ${fixture.away_team}`, selection: `${fixture.home_team} (First Goal)`, odds: parseFloat(firstGoal.home), marketType: 'first_goal', sport: 'football' })} />
                                                    <OddsCard title="No Goal" odds={firstGoal.no_goal} oddsFormat={oddsFormat}
                                                        isSelected={isBetSelected('No Goal', 'first_goal')}
                                                        onSelect={() => addToBetSlip({ matchId: fixture.id, match: `${fixture.home_team} vs ${fixture.away_team}`, selection: 'No Goal', odds: parseFloat(firstGoal.no_goal), marketType: 'first_goal', sport: 'football' })} />
                                                    <OddsCard title={fixture.away_team} odds={firstGoal.away} oddsFormat={oddsFormat}
                                                        isSelected={isBetSelected(`${fixture.away_team} (First Goal)`, 'first_goal')}
                                                        onSelect={() => addToBetSlip({ matchId: fixture.id, match: `${fixture.home_team} vs ${fixture.away_team}`, selection: `${fixture.away_team} (First Goal)`, odds: parseFloat(firstGoal.away), marketType: 'first_goal', sport: 'football' })} />
                                                </div>
                                            )}
                                            
                                            {/* HT/FT */}
                                            {market.id === 'ht_ft' && Object.keys(halfTimeFullTime).length > 0 && (
                                                <div className="grid grid-cols-2 xs:grid-cols-3 gap-2 sm:gap-3">
                                                    {Object.entries(halfTimeFullTime).slice(0, 9).map(([result, oddsVal]) => (
                                                        <OddsCard key={result} title={result} odds={oddsVal} oddsFormat={oddsFormat} compact
                                                            isSelected={isBetSelected(result, 'ht_ft')}
                                                            onSelect={() => addToBetSlip({ matchId: fixture.id, match: `${fixture.home_team} vs ${fixture.away_team}`, selection: result, odds: parseFloat(oddsVal), marketType: 'ht_ft', sport: 'football' })} />
                                                    ))}
                                                </div>
                                            )}
                                            
                                            {/* Total Goals */}
                                            {market.id === 'total_goals' && Object.keys(totalGoals).length > 0 && (
                                                <div className="grid grid-cols-2 xs:grid-cols-3 gap-2 sm:gap-3">
                                                    {Object.entries(totalGoals).map(([marketName, oddsVal]) => (
                                                        <OddsCard key={marketName} title={marketName.replace(/_/g, ' ')} odds={oddsVal} oddsFormat={oddsFormat} compact
                                                            isSelected={isBetSelected(marketName, 'total_goals')}
                                                            onSelect={() => addToBetSlip({ matchId: fixture.id, match: `${fixture.home_team} vs ${fixture.away_team}`, selection: marketName, odds: parseFloat(oddsVal), marketType: 'total_goals', sport: 'football' })} />
                                                    ))}
                                                </div>
                                            )}
                                        </div>
                                    )}
                                </div>
                            );
                        })}
                    </div>
                )}
                
                {/* Head to Head Tab */}
                {activeTab === 'h2h' && (
                    <div className="bg-gray-900 rounded-xl p-4 sm:p-6">
                        {h2h ? (
                            <div className="space-y-4 sm:space-y-6">
                                {/* Stats Grid */}
                                <div className="grid grid-cols-3 gap-2 sm:gap-4 text-center">
                                    <div className="bg-gray-800 rounded-lg p-3 sm:p-4">
                                        <div className="text-xl sm:text-3xl font-bold text-green-500">{h2h.home_wins || 0}</div>
                                        <div className="text-xs sm:text-sm text-gray-400 mt-1">{fixture.home_team} Wins</div>
                                    </div>
                                    <div className="bg-gray-800 rounded-lg p-3 sm:p-4">
                                        <div className="text-xl sm:text-3xl font-bold text-yellow-500">{h2h.draws || 0}</div>
                                        <div className="text-xs sm:text-sm text-gray-400 mt-1">Draws</div>
                                    </div>
                                    <div className="bg-gray-800 rounded-lg p-3 sm:p-4">
                                        <div className="text-xl sm:text-3xl font-bold text-blue-500">{h2h.away_wins || 0}</div>
                                        <div className="text-xs sm:text-sm text-gray-400 mt-1">{fixture.away_team} Wins</div>
                                    </div>
                                </div>
                                
                                {/* Goals Summary */}
                                <div className="bg-gray-800 rounded-lg p-4">
                                    <h4 className="text-xs sm:text-sm font-semibold text-gray-400 mb-3">Goals Summary</h4>
                                    <div className="space-y-2 text-sm sm:text-base">
                                        <div className="flex justify-between items-center">
                                            <span className="text-gray-400">Total Goals:</span>
                                            <span className="text-white font-bold">{(h2h.home_goals || 0) + (h2h.away_goals || 0)}</span>
                                        </div>
                                        <div className="flex justify-between items-center">
                                            <span className="text-gray-400">{fixture.home_team} Goals:</span>
                                            <span className="text-green-500 font-bold">{h2h.home_goals || 0}</span>
                                        </div>
                                        <div className="flex justify-between items-center">
                                            <span className="text-gray-400">{fixture.away_team} Goals:</span>
                                            <span className="text-blue-500 font-bold">{h2h.away_goals || 0}</span>
                                        </div>
                                        <div className="flex justify-between items-center">
                                            <span className="text-gray-400">Avg Goals/Match:</span>
                                            <span className="text-white font-bold">
                                                {h2h.total_matches ? (((h2h.home_goals || 0) + (h2h.away_goals || 0)) / h2h.total_matches).toFixed(2) : 0}
                                            </span>
                                        </div>
                                    </div>
                                </div>
                                
                                {/* Last Meeting */}
                                {h2h.last_meeting_date && (
                                    <div className="bg-gray-800 rounded-lg p-4">
                                        <h4 className="text-xs sm:text-sm font-semibold text-gray-400 mb-3">Last Meeting</h4>
                                        <div className="flex justify-between items-center text-sm sm:text-base">
                                            <span className="text-gray-400">Date:</span>
                                            <span className="text-white">{new Date(h2h.last_meeting_date).toLocaleDateString()}</span>
                                        </div>
                                        {h2h.last_meeting_result && (
                                            <div className="flex justify-between items-center mt-2 text-sm sm:text-base">
                                                <span className="text-gray-400">Result:</span>
                                                <span className="text-yellow-500 font-bold">{h2h.last_meeting_result}</span>
                                            </div>
                                        )}
                                    </div>
                                )}
                                
                                {/* Recent Form */}
                                <div className="bg-gray-800 rounded-lg p-4">
                                    <h4 className="text-xs sm:text-sm font-semibold text-gray-400 mb-3">Recent Form (Last 5)</h4>
                                    <div className="space-y-3">
                                        <div>
                                            <div className="flex items-center justify-between mb-1">
                                                <span className="text-xs sm:text-sm text-gray-400">{fixture.home_team}</span>
                                                <div className="flex gap-1">
                                                    {['W', 'W', 'D', 'L', 'W'].map((result, i) => (
                                                        <span key={i} className={`w-5 h-5 sm:w-6 sm:h-6 rounded-full flex items-center justify-center text-[10px] sm:text-xs font-bold ${
                                                            result === 'W' ? 'bg-green-600' : result === 'D' ? 'bg-yellow-600' : 'bg-red-600'
                                                        }`}>
                                                            {result}
                                                        </span>
                                                    ))}
                                                </div>
                                            </div>
                                        </div>
                                        <div>
                                            <div className="flex items-center justify-between mb-1">
                                                <span className="text-xs sm:text-sm text-gray-400">{fixture.away_team}</span>
                                                <div className="flex gap-1">
                                                    {['L', 'W', 'D', 'W', 'L'].map((result, i) => (
                                                        <span key={i} className={`w-5 h-5 sm:w-6 sm:h-6 rounded-full flex items-center justify-center text-[10px] sm:text-xs font-bold ${
                                                            result === 'W' ? 'bg-green-600' : result === 'D' ? 'bg-yellow-600' : 'bg-red-600'
                                                        }`}>
                                                            {result}
                                                        </span>
                                                    ))}
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        ) : (
                            <div className="text-center py-8 sm:py-12">
                                <FiCornerUpRight className="w-10 h-10 sm:w-12 sm:h-12 text-gray-600 mx-auto mb-3" />
                                <p className="text-gray-400 text-sm sm:text-base">Head-to-head statistics not available</p>
                            </div>
                        )}
                    </div>
                )}
                
                {/* Match Stats Tab */}
                {activeTab === 'stats' && (
                    <div className="bg-gray-900 rounded-xl p-4 sm:p-6">
                        {isLive ? (
                            <div className="space-y-4 sm:space-y-6">
                                {/* Possession */}
                                <div>
                                    <div className="flex justify-between text-xs sm:text-sm mb-2">
                                        <span className="text-gray-400">{fixture.home_team}</span>
                                        <span className="text-white font-bold">{matchStats.possession.home}%</span>
                                        <span className="text-gray-400">{fixture.away_team}</span>
                                    </div>
                                    <div className="flex h-2 rounded-full overflow-hidden">
                                        <div className="bg-green-500" style={{ width: `${matchStats.possession.home}%` }}></div>
                                        <div className="bg-blue-500" style={{ width: `${matchStats.possession.away}%` }}></div>
                                    </div>
                                </div>
                                
                                {/* Stats Grid */}
                                <div className="grid grid-cols-2 xs:grid-cols-3 gap-2 sm:gap-4 text-center">
                                    <StatItem label="Shots" home={matchStats.shots.home} away={matchStats.shots.away} />
                                    <StatItem label="Shots on Target" home={matchStats.shotsOnTarget.home} away={matchStats.shotsOnTarget.away} />
                                    <StatItem label="Corners" home={matchStats.corners.home} away={matchStats.corners.away} />
                                    <StatItem label="Fouls" home={matchStats.fouls.home} away={matchStats.fouls.away} />
                                    <StatItem label="Yellow Cards" home={matchStats.yellowCards.home} away={matchStats.yellowCards.away} />
                                    <StatItem label="Red Cards" home={matchStats.redCards.home} away={matchStats.redCards.away} />
                                </div>
                            </div>
                        ) : (
                            <div className="text-center py-8 sm:py-12">
                                <FiPieChart className="w-10 h-10 sm:w-12 sm:h-12 text-gray-600 mx-auto mb-3" />
                                <p className="text-gray-400 text-sm sm:text-base">Live match statistics will appear here during the match</p>
                            </div>
                        )}
                    </div>
                )}
                
                {/* Lineups Tab */}
                {activeTab === 'lineups' && (
                    <div className="bg-gray-900 rounded-xl p-4 sm:p-6">
                        {lineups && lineups.length > 0 ? (
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6">
                                {lineups.map((lineup, idx) => (
                                    <div key={idx} className="bg-gray-800 rounded-lg p-3 sm:p-4">
                                        <div className="flex items-center gap-2 mb-3 pb-2 border-b border-gray-700">
                                            <FiUsers className="w-4 h-4 text-blue-500" />
                                            <h4 className="font-bold text-white text-sm sm:text-base">{lineup.team_name}</h4>
                                            {lineup.formation && (
                                                <span className="text-[10px] sm:text-xs text-gray-400 ml-auto">Formation: {lineup.formation}</span>
                                            )}
                                        </div>
                                        {lineup.coach_name && (
                                            <p className="text-xs sm:text-sm text-gray-400 mb-3 flex items-center gap-1">
                                                <FiUser className="w-3 h-3" />
                                                Coach: {lineup.coach_name}
                                            </p>
                                        )}
                                        {lineup.starting_xi && (
                                            <div>
                                                <p className="text-[10px] sm:text-xs text-gray-500 mb-2 flex items-center gap-1">
                                                    <FiFlag className="w-3 h-3" />
                                                    Starting XI:
                                                </p>
                                                <div className="space-y-1">
                                                    {JSON.parse(lineup.starting_xi).slice(0, 11).map((player, i) => (
                                                        <div key={i} className="text-xs sm:text-sm text-gray-300 flex items-center gap-2">
                                                            <span className="text-[10px] sm:text-xs text-gray-500 w-5 sm:w-6">{player.number || i + 1}</span>
                                                            <span className="truncate">{player.name}</span>
                                                            {player.position && (
                                                                <span className="text-[10px] sm:text-xs text-gray-500 ml-auto">{player.position}</span>
                                                            )}
                                                        </div>
                                                    ))}
                                                </div>
                                            </div>
                                        )}
                                        {lineup.substitutes && JSON.parse(lineup.substitutes).length > 0 && (
                                            <div className="mt-3 pt-3 border-t border-gray-700">
                                                <p className="text-[10px] sm:text-xs text-gray-500 mb-2">Substitutes:</p>
                                                <div className="flex flex-wrap gap-1">
                                                    {JSON.parse(lineup.substitutes).slice(0, 7).map((player, i) => (
                                                        <span key={i} className="text-[10px] sm:text-xs text-gray-400 bg-gray-700 px-2 py-0.5 rounded">
                                                            {player.name}
                                                        </span>
                                                    ))}
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                ))}
                            </div>
                        ) : (
                            <div className="text-center py-8 sm:py-12">
                                <FiUsers className="w-10 h-10 sm:w-12 sm:h-12 text-gray-600 mx-auto mb-3" />
                                <p className="text-gray-400 text-sm sm:text-base">Lineups not yet available</p>
                            </div>
                        )}
                    </div>
                )}
                
                {/* More Matches Banner */}
                <div className="mt-6 sm:mt-8 bg-gradient-to-r from-blue-900/30 to-blue-800/20 border border-blue-700/30 rounded-xl p-4 sm:p-6 text-center">
                    <h3 className="text-base sm:text-lg font-bold text-white mb-2">More Matches</h3>
                    <p className="text-gray-300 text-xs sm:text-sm mb-4">Explore other football matches from today</p>
                    <Link to="/sports" className="inline-block bg-blue-600 hover:bg-blue-700 text-white px-4 sm:px-6 py-1.5 sm:py-2 rounded-lg font-semibold text-xs sm:text-sm">
                        View All Matches
                    </Link>
                </div>
            </div>
            
            <Footer />
        </div>
    );
};

// Odds Card Component
const OddsCard = ({ title, odds, oddsFormat, compact = false, isSelected = false, onSelect }) => {
    if (!odds || odds === null) return null;
    
    const formattedOdds = formatOddsValue(odds, oddsFormat);
    
    return (
        <button
            onClick={onSelect}
            className={`rounded-lg text-center transition-all duration-200 ${
                isSelected 
                    ? 'bg-green-600 text-white shadow-lg shadow-green-600/30 scale-105' 
                    : 'bg-gray-800 text-white hover:bg-green-600/80 hover:scale-105'
            } ${compact ? 'p-1.5 sm:p-2' : 'p-2 sm:p-3'}`}
        >
            <div className={`font-medium ${compact ? 'text-[10px] sm:text-xs' : 'text-xs sm:text-sm'} truncate`}>{title}</div>
            <div className={`font-bold mt-0.5 sm:mt-1 ${compact ? 'text-xs sm:text-sm' : 'text-base sm:text-lg'}`}>{formattedOdds}</div>
            {isSelected && <div className="text-[8px] sm:text-[10px] opacity-80 mt-0.5">✓ Added</div>}
        </button>
    );
};

// Stat Item Component
const StatItem = ({ label, home, away }) => (
    <div className="bg-gray-800 rounded-lg p-2 sm:p-3">
        <div className="text-[10px] sm:text-xs text-gray-400 mb-1 sm:mb-2">{label}</div>
        <div className="flex justify-between items-center">
            <span className="text-base sm:text-lg font-bold text-green-500">{home}</span>
            <span className="text-[10px] sm:text-xs text-gray-500">|</span>
            <span className="text-base sm:text-lg font-bold text-blue-500">{away}</span>
        </div>
    </div>
);

export default MatchDetailPage;